import { Pressable, Text } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import { ChevronLeft } from "lucide-react-native";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type BackLinkTone = "default" | "onDark";

export type BackLinkProps = {
  /** Where the link goes, for example "Back", "Care" or "Clinics". */
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  /** `onDark` is for dark backgrounds such as onboarding. */
  tone?: BackLinkTone;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
};

const TONE_COLOR: Record<BackLinkTone, string> = {
  default: semanticColors.text.link,
  onDark: semanticColors.onDark.text.secondary,
};

/**
 * The "‹ Back" link at the top of a screen or a step: a left chevron and the
 * name of the place it returns to.
 */
export function BackLink({
  label,
  onPress,
  accessibilityLabel,
  action,
  tone = "default",
  disabled = false,
  style,
}: BackLinkProps) {
  const capturePress = usePressCapture("BackLink");
  const color = TONE_COLOR[tone];
  return (
    <Pressable
      onPress={() => {
        if (disabled) return;
        capturePress(pressLabel(label, accessibilityLabel), action ?? "back");
        onPress();
      }}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      hitSlop={8}
      style={({ pressed }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          alignSelf: "flex-start",
          gap: 4,
          opacity: disabled ? 0.44 : pressed ? 0.66 : 1,
        },
        style,
      ]}
    >
      <ChevronLeft size={18} color={color} strokeWidth={1.5} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          fontWeight: "500",
          fontFamily: typography.families.sans,
          color,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export default BackLink;
